import { buildDocumentTitle, PUBLIC_BRAND_WORDMARK } from '@core/branding/public-brand';
import {
  buildOpeningHoursSpecification,
  parseBusinessHoursField,
} from '@shared/utils/business-hours.utils';
import { summarizeBusinessHours } from '@shared/utils/store-landing-operational.utils';
import {
  PublicPageSeoConfig,
  PublicPageSeoPageKind,
  PublicPageSeoProductEntry,
} from './public-page-seo.models';
import { buildPublicCanonicalUrl } from './public-page-seo-canonical.utils';

const META_DESCRIPTION_MAX = 160;

const CATALOG_PAGE_SUFFIX = 'Catálogo';
const SERVICES_PAGE_SUFFIX = 'Servicios';

/** Recorta y normaliza espacios para meta description. */
export function truncateMetaDescription(value: string, maxLength = META_DESCRIPTION_MAX): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return '';
  }
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

export function buildPublicStoreDocumentTitle(
  storeName: string,
  pageKind: PublicPageSeoPageKind
): string {
  const name = storeName.trim();
  if (!name) {
    return buildDocumentTitle(null);
  }
  if (pageKind === 'catalog') {
    return buildDocumentTitle(`${name} — ${CATALOG_PAGE_SUFFIX}`);
  }
  if (pageKind === 'services') {
    return buildDocumentTitle(`${name} — ${SERVICES_PAGE_SUFFIX}`);
  }
  return buildDocumentTitle(name);
}

export function buildPublicStoreDescription(
  storeName: string,
  description?: string | null,
  pageKind: PublicPageSeoPageKind = 'landing'
): string {
  const bio = truncateMetaDescription(description ?? '');
  if (bio) {
    return bio;
  }

  const name = storeName.trim() || 'este negocio';
  if (pageKind === 'catalog') {
    return truncateMetaDescription(
      `Explora el menú y productos de ${name}. Pide en línea desde ${PUBLIC_BRAND_WORDMARK}.`
    );
  }
  if (pageKind === 'services') {
    return truncateMetaDescription(
      `Servicios de ${name}. Solicita cotización o visita desde ${PUBLIC_BRAND_WORDMARK}.`
    );
  }

  return truncateMetaDescription(
    `Página oficial de ${name}. Enlaces, contacto y catálogo en ${PUBLIC_BRAND_WORDMARK}.`
  );
}

export function resolvePublicShareImageUrl(
  bannerUrl?: string | null,
  logoUrl?: string | null
): string | null {
  const banner = bannerUrl?.trim();
  if (banner) {
    return banner;
  }
  const logo = logoUrl?.trim();
  return logo || null;
}

export { buildPublicCanonicalUrl };

function buildLocalBusinessNode(
  config: PublicPageSeoConfig,
  canonicalUrl: string,
  description: string,
  image: string | null
): Record<string, unknown> {
  const name = config.storeName.trim() || PUBLIC_BRAND_WORDMARK;
  const address = config.address?.trim();
  const city = config.cityLabel?.trim();
  const businessHours = summarizeBusinessHours(config.businessHours);
  const parsedHours = parseBusinessHoursField(config.businessHours);
  const openingHoursSpecification = parsedHours.structured
    ? buildOpeningHoursSpecification(parsedHours.structured)
    : [];
  const sameAs = (config.pageLinkUrls ?? [])
    .map((url) => url.trim())
    .filter((url) => url.startsWith('http'));

  return {
    '@type': 'LocalBusiness',
    name,
    url: canonicalUrl,
    description,
    ...(image ? { image } : {}),
    ...(config.whatsapp?.trim() ? { telephone: config.whatsapp.trim() } : {}),
    ...(sameAs.length > 0 ? { sameAs } : {}),
    ...(address || city
      ? {
          address: {
            '@type': 'PostalAddress',
            ...(address ? { streetAddress: address } : {}),
            ...(city ? { addressLocality: city } : {}),
            addressCountry: 'CO',
          },
        }
      : {}),
    ...(businessHours ? { openingHours: businessHours } : {}),
    ...(openingHoursSpecification.length > 0 ? { openingHoursSpecification } : {}),
  };
}

function buildProductListNodes(
  products: readonly PublicPageSeoProductEntry[],
  canonicalUrl: string
): Record<string, unknown>[] {
  return products.slice(0, 20).map((product, index) => {
    const offers =
      product.price != null && product.price > 0
        ? {
            '@type': 'Offer',
            price: product.price,
            priceCurrency: product.currency?.trim() || 'COP',
            availability: 'https://schema.org/InStock',
            url: canonicalUrl,
          }
        : undefined;

    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: product.name,
        ...(product.description?.trim() ? { description: product.description.trim() } : {}),
        ...(product.imageUrl?.trim() ? { image: product.imageUrl.trim() } : {}),
        ...(offers ? { offers } : {}),
      },
    };
  });
}

export function buildPublicPageJsonLd(config: PublicPageSeoConfig, canonicalUrl: string): string {
  const name = config.storeName.trim() || PUBLIC_BRAND_WORDMARK;
  const description = buildPublicStoreDescription(
    config.storeName,
    config.description,
    config.pageKind
  );
  const image = resolvePublicShareImageUrl(config.bannerUrl, config.logoUrl);
  const localBusiness = buildLocalBusinessNode(config, canonicalUrl, description, image);

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebSite',
      name,
      url: canonicalUrl,
      description,
      publisher: {
        '@type': 'Organization',
        name: PUBLIC_BRAND_WORDMARK,
      },
    },
    localBusiness,
  ];

  const products = config.products ?? [];
  if (config.pageKind === 'catalog' && products.length > 0) {
    graph.push({
      '@type': 'ItemList',
      name: `${name} — productos`,
      itemListElement: buildProductListNodes(products, canonicalUrl),
    });
  }

  const payload: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@graph': graph,
  };

  return JSON.stringify(payload);
}
